import { escapeHtml } from '../util/escapeHtml.js';
import { anchored } from '../util/viewportAnchor.js';
import { mountShell } from '../components/Shell.js';
import { flashToast } from '../util/toast.js';
import { api, HttpError } from '../api/client.js';
import { store } from '../state/store.js';
import { makeCollapsible } from '../util/collapsible.js';
import { formatCrowns, formatBigNum } from '../util/formatBig.js';
import { cgTable, cgCol, cgRow } from '../util/costReward.js';
import { attachPerkInfoToggle } from '../components/PerkInfoPanel.js';
import { perkName } from '@rex-mortis/shared';
import type {
  ActiveEnrollmentDto,
  CourseListing,
} from '@rex-mortis/shared';

interface FlashState {
  kind: 'success' | 'danger' | 'warn';
  message: string;
}

/** Per-course legacy-perk stacking progress, keyed by perk slug. */
interface PerkProgress {
  completions: number;
  max: number;
}

const TIER_ORDER: CourseListing['tier'][] = [
  'novice',
  'apprentice',
  'master',
  'royal',
  'vigil',
  'season',
  'pilgrimage',
  'consecration',
  'apotheosis',
  'empyrean',
];
const TIER_LABEL: Record<CourseListing['tier'], string> = {
  novice: 'Novice',
  apprentice: 'Apprentice',
  master: 'Master',
  royal: 'Royal',
  vigil: 'Vigil',
  season: 'Season',
  pilgrimage: 'Pilgrimage',
  consecration: 'Consecration',
  apotheosis: 'Apotheosis',
  empyrean: 'Empyrean',
};

let countdownTimer: number | null = null;

function clearCountdown(): void {
  if (countdownTimer !== null) {
    window.clearInterval(countdownTimer);
    countdownTimer = null;
  }
}

/** True if a course grants any permanent resource-cap raise. */
function hasLegacy(c: CourseListing): boolean {
  const l = c.legacy;
  return !!(l.vigorMax || l.resolveMax || l.spiritMax || l.lifeMax);
}

export async function renderScholarium(root: HTMLElement): Promise<void> {
  let courses: CourseListing[] = [];
  let active: ActiveEnrollmentDto | null = null;
  // Owned perks (any slug present) + legacy stacking progress by slug.
  const ownedPerks = new Set<string>();
  const perkProgress = new Map<string, PerkProgress>();

  /** Re-pull the player-specific dynamic state (active course + owned perks). */
  async function refreshDynamic(): Promise<void> {
    try {
      active = (await api.activeEnrollment()).active;
    } catch {
      /* tolerated */
    }
    try {
      const p = await api.getPerks();
      ownedPerks.clear();
      perkProgress.clear();
      for (const perk of p.perks) {
        ownedPerks.add(perk.slug);
        if (perk.legacy) {
          perkProgress.set(perk.slug, { completions: perk.completions, max: perk.max });
        }
      }
    } catch {
      /* tolerated */
    }
  }

  try {
    const me = await api.me();
    store.set({ account: me.account, character: me.character });
    const cat = await api.listCourses();
    courses = cat.courses;
    await refreshDynamic();
  } catch {
    // tolerated
  }

  let flash: FlashState | null = null;

  mountShell(root, (main) => {
    const renderMain = () => {
      const char = store.get().character;
      if (!char) {
        main.innerHTML = '<p>Loading…</p>';
        return;
      }

      main.innerHTML = `
        <img class="page-banner" src="/img/banners/scholarium.webp" alt="" />
        <h1>The Scholarium</h1>
        <p style="font-style: italic; color: var(--c-ink-soft); margin-top: -8px;">
          Knowledge stays. Crowns come and go.
        </p>

        <div id="flash"></div>
        <div id="active-panel"></div>
        <div id="cats"></div>
      `;

      const style = document.createElement('style');
      style.textContent = `
        .tier-section { margin-bottom: var(--s-5); }
        .tier-section h2 {
          font-family: var(--font-display);
          color: var(--c-oxblood);
          border-bottom: 1px solid var(--c-rule);
          padding-bottom: var(--s-1);
          margin-bottom: var(--s-3);
        }
        .course-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: var(--s-3);
        }
        .course-card {
          background: var(--c-parchment);
          border: 1px solid var(--c-rule);
          border-radius: var(--radius);
          padding: var(--s-3);
          display: flex;
          flex-direction: column;
        }
        .course-card.active-course {
          border-color: var(--c-gold);
          box-shadow: 0 0 0 1px var(--c-gold) inset;
        }
        .course-card.earned { opacity: 0.92; }
        .course-card h3 { margin: 0 0 4px 0; font-size: 1.05rem; }
        .course-card h3 .badge-done {
          font-family: var(--font-display);
          font-size: 0.68rem;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: var(--c-success);
          border: 1px solid var(--c-success);
          border-radius: 999px;
          padding: 1px 7px;
          margin-left: 6px;
          vertical-align: middle;
        }
        .course-card h3 .badge-studying {
          font-family: var(--font-display);
          font-size: 0.68rem;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: var(--c-on-gold);
          background: var(--c-gold);
          border-radius: 999px;
          padding: 1px 7px;
          margin-left: 6px;
          vertical-align: middle;
        }
        .course-card .blurb {
          font-style: italic;
          color: var(--c-ink-soft);
          font-size: 0.92rem;
          margin-bottom: var(--s-2);
        }
        /* Table hugs the bottom of the blurb text; the button is pushed to the
           card's bottom edge so buttons stay aligned across the row. */
        .course-card .cg { margin-bottom: auto; }
        .cg-struck { text-decoration: line-through; opacity: 0.55; }
        .cg-note {
          font-size: 0.74rem;
          color: var(--c-success);
          line-height: 1.4;
          margin-top: 3px;
        }
        .schol-prog {
          font-family: var(--font-numeric);
          font-size: 0.8rem;
          color: var(--c-ink-soft);
          border: 1px solid var(--c-rule-soft);
          border-radius: 999px;
          padding: 0 6px;
          margin-left: 6px;
        }
        .schol-prog.maxed { color: var(--c-gold-text); border-color: var(--c-gold); }
        .progress-bar {
          background: var(--c-parchment-dark);
          border: 1px solid var(--c-rule);
          border-radius: var(--radius);
          height: 18px;
          overflow: hidden;
          margin: var(--s-2) 0;
        }
        .progress-fill {
          background: linear-gradient(90deg, var(--c-gold) 0%, var(--c-gold-bright) 100%);
          height: 100%;
          transition: width 0.5s;
        }
        .schol-active .pill {
          background: var(--c-parchment-dark);
          border: 1px solid var(--c-rule-soft);
          border-radius: 999px;
          padding: 2px 10px;
          font-family: var(--font-numeric);
          font-size: 0.85rem;
        }
        .schol-active .pill.success { border-color: var(--c-success); color: var(--c-success); }
      `;
      main.appendChild(style);

      // Flash → floating toast (overlays in place; no scroll-to-top).
      if (flash) flashToast(flash);

      // ── Active-course progress panel (inline; catalogue stays visible) ────
      const activeRoot = main.querySelector<HTMLDivElement>('#active-panel')!;
      if (active) {
        const def = courses.find((c) => c.key === active!.courseKey) ?? null;
        const startMs = Date.parse(active.startedAt);
        const endMs = Date.parse(active.completesAt);
        const totalMs = Math.max(1, endMs - startMs);
        const elapsedMs = Math.min(totalMs, Math.max(0, Date.now() - startMs));
        const pct = active.ready ? 100 : Math.min(100, (elapsedMs / totalMs) * 100);

        activeRoot.innerHTML = `
          <section class="panel schol-active">
            <h2 style="margin-top:0;">Studying: ${escapeHtml(active.courseName)}</h2>
            ${def ? `<p style="font-style:italic; color: var(--c-ink-soft);">"${escapeHtml(def.blurb)}"</p>` : ''}
            <div class="progress-bar"><div class="progress-fill" id="schol-fill" style="width: ${pct.toFixed(1)}%;"></div></div>
            <div style="display:flex; flex-wrap:wrap; gap: var(--s-2); margin-top: var(--s-2);">
              ${def ? `<span class="pill">${formatDuration(def.durationSeconds)}</span>` : ''}
              <span class="pill ${active.ready ? 'success' : ''}" id="cdline">${
                active.ready
                  ? 'Ready to claim'
                  : `<span id="cd">${formatRemaining(active.completesAt)}</span> remaining`
              }</span>
            </div>
            <div style="margin-top: var(--s-3);">
              <button class="btn" id="claim" ${active.ready ? '' : 'disabled'}>
                ${active.ready ? 'Claim rewards' : 'Not ready'}
              </button>
            </div>
          </section>
        `;
      }

      // Wire claim button.
      const claimBtn = main.querySelector<HTMLButtonElement>('#claim');
      claimBtn?.addEventListener('click', async () => {
        if (!active) return;
        claimBtn.disabled = true;
        try {
          const r = await api.claimCourse({ enrollmentId: active.id });
          store.set({ character: r.character });
          const parts: string[] = [`${r.courseName} complete.`];
          const gainsParts: string[] = [];
          if (r.gains.toil) gainsParts.push(`+${formatBigNum(r.gains.toil)} toil`);
          if (r.gains.wits) gainsParts.push(`+${formatBigNum(r.gains.wits)} wits`);
          if (r.gains.endurance) gainsParts.push(`+${formatBigNum(r.gains.endurance)} endurance`);
          if (r.xpGained) gainsParts.push(`+${formatBigNum(r.xpGained)} XP`);
          if (gainsParts.length) parts.push(gainsParts.join(', ') + '.');
          if (r.levelsGained > 0) {
            parts.push(`${r.levelsGained} level${r.levelsGained === 1 ? '' : 's'} gained!`);
          }
          // Legacy: the permanent, once-only cap raise.
          const legacyParts: string[] = [];
          if (r.legacy.vigorMax) legacyParts.push(`+${r.legacy.vigorMax} max vigor`);
          if (r.legacy.resolveMax) legacyParts.push(`+${r.legacy.resolveMax} max resolve`);
          if (r.legacy.spiritMax) legacyParts.push(`+${r.legacy.spiritMax} max spirit`);
          if (r.legacy.lifeMax) legacyParts.push(`+${r.legacy.lifeMax} max life`);
          if (legacyParts.length) {
            parts.push(`Legacy etched: ${legacyParts.join(', ')} — permanent.`);
          } else if (r.legacyCapped) {
            parts.push(`Max bonus already earned — repeats give working stats + XP only.`);
          }
          if (r.perk && !r.perk.alreadyOwned) parts.push(`Perk unlocked: ${perkName(r.perk.slug)}.`);
          else if (r.perk?.alreadyOwned) parts.push(`(Perk ${perkName(r.perk.slug)} already known — no double-grant.)`);
          flash = { kind: 'success', message: parts.join(' ') };
          await refreshDynamic();
        } catch (err) {
          const msg = err instanceof HttpError ? err.message : 'Claim failed.';
          flash = { kind: 'danger', message: msg };
        } finally {
          anchored(renderMain);
        }
      });

      // ── Catalogue (always visible) ───────────────────────────────────────
      const catsRoot = main.querySelector<HTMLDivElement>('#cats')!;
      for (const tier of TIER_ORDER) {
        const inTier = courses.filter((c) => c.tier === tier);
        if (inTier.length === 0) continue;

        const section = document.createElement('section');
        section.className = 'tier-section';

        const h2 = document.createElement('h2');
        h2.textContent = TIER_LABEL[tier];
        section.appendChild(h2);

        const grid = document.createElement('div');
        grid.className = 'course-grid';

        for (const course of inTier) {
          const charCrowns = BigInt(char.crowns);
          const cost = BigInt(course.costCrowns);
          const canAfford = charCrowns >= cost;

          const legacyCourse = hasLegacy(course);
          const perkOwned = course.perkSlug ? ownedPerks.has(course.perkSlug) : false;
          // Legacy resource-cap raise stacks across the first 5 completions
          // (max from prog.max, defaulting to 5). Only show the row as struck
          // when the stack count is fully maxed; partial stacks still display
          // as a gain (you can still earn more). One-shot courses (Pilgrimage+)
          // hit the cap on first claim and the `oneShotLocked` path below
          // handles the cross-out for the rest of the card.
          const prog = course.perkSlug ? perkProgress.get(course.perkSlug) : undefined;
          const legacyMaxed =
            legacyCourse && (prog?.completions ?? 0) >= (prog?.max ?? 5);
          const legacyEarned = legacyMaxed;
          // A one-time-perk course (no legacy) is "complete" once its perk is owned.
          const oneTimeDone = !!course.perkSlug && !legacyCourse && perkOwned;
          // Pilgrimage+: the whole course is locked after the first claim — no
          // working-stat farm, no XP farm, nothing. The server enforces this;
          // the UI mirrors it by greying the card and disabling Enrol.
          const oneShotLocked = course.oneShot && course.completed;
          const isStudying = active?.courseKey === course.key;

          const card = document.createElement('div');
          card.className =
            'course-card' +
            (isStudying ? ' active-course' : '') +
            (oneTimeDone || oneShotLocked ? ' earned' : '');

          // Requires box: what enrolling costs.
          const reqRows = [
            cgRow('Crowns', formatCrowns(course.costCrowns), canAfford ? '' : 'warn'),
            cgRow('Time', formatDuration(course.durationSeconds)),
          ];

          // Reward box: stat gains, XP, perk, and the once-only legacy bonuses.
          // For one-shot courses already completed, every reward is sunk — render
          // each row crossed out so the card matches the "Completed" badge.
          const rewardRows: string[] = [];
          const struckRow = (label: string, val: string): string =>
            cgRow(`<span class="cg-struck">${label}</span>`, `<span class="cg-struck">${val}</span>`);
          const gainRow = (label: string, val: string): string =>
            oneShotLocked ? struckRow(label, val) : cgRow(label, val, 'gain');
          if (course.gains.toil) rewardRows.push(gainRow('Toil', `+${formatBigNum(course.gains.toil)}`));
          if (course.gains.wits) rewardRows.push(gainRow('Wits', `+${formatBigNum(course.gains.wits)}`));
          if (course.gains.endurance) rewardRows.push(gainRow('Endurance', `+${formatBigNum(course.gains.endurance)}`));
          if (course.xpReward) rewardRows.push(gainRow('XP', `+${formatBigNum(course.xpReward)}`));

          if (course.perkSlug) {
            const chip = `<span class="schol-perk" data-slug="${escapeHtml(course.perkSlug)}">${escapeHtml(perkName(course.perkSlug))}</span>`;
            // The perk chip gets its own cell — keep it the only thing there so
            // it never collides with the label in the narrow reward column.
            rewardRows.push(cgRow('Perk', chip, 'gain'));
            // Legacy "Stacks x/5" counter — only meaningful for repeatable
            // legacy courses (Vigil / Season). One-shot tiers (Pilgrimage /
            // Consecration / Apotheosis) can never re-claim, so there is
            // nothing to stack and the row is pure noise — skip it.
            if (legacyCourse && !course.oneShot) {
              const c = prog?.completions ?? 0;
              const m = prog?.max ?? 5;
              rewardRows.push(cgRow('Stacks', `${c}/${m}`, c >= m ? 'gain' : ''));
            } else if (perkOwned) {
              // One-time perk already earned — the card's "Completed" title
              // badge already signals this, so no extra per-row tag is needed.
            }
          }

          // Legacy cap raises: granted ONCE. Cross them out once earned; a single
          // note below explains it (per-row tags overflowed the narrow column).
          const legacyRows: Array<[string, number]> = [
            ['Max vigor ✦', course.legacy.vigorMax],
            ['Max resolve ✦', course.legacy.resolveMax],
            ['Max spirit ✦', course.legacy.spiritMax],
            ['Max life ✦', course.legacy.lifeMax],
          ];
          for (const [label, val] of legacyRows) {
            if (!val) continue;
            if (legacyEarned) {
              // Earned once already — show the cap raise struck through. The
              // full explanation of how Legacy caps work now lives in the
              // Compendium (Scholarium chapter), reachable via the page's help
              // link, so we no longer clutter every card with a green note.
              rewardRows.push(
                cgRow(`<span class="cg-struck">${label}</span>`, `<span class="cg-struck">+${formatBigNum(val)}</span>`),
              );
            } else {
              rewardRows.push(cgRow(label, `+${formatBigNum(val)}`, 'gain'));
            }
          }

          // Enrol button state.
          let btnLabel: string;
          let btnDisabled: boolean;
          if (isStudying) {
            btnLabel = 'In progress';
            btnDisabled = true;
          } else if (oneShotLocked) {
            // Pilgrimage+ already claimed — permanently locked.
            btnLabel = 'Completed — once in a lifetime';
            btnDisabled = true;
          } else if (active) {
            btnLabel = 'Finish current course';
            btnDisabled = true;
          } else if (!canAfford) {
            btnLabel = 'Not enough crowns';
            btnDisabled = true;
          } else {
            btnLabel = oneTimeDone ? 'Study again (stats + XP)' : 'Enrol';
            btnDisabled = false;
          }

          const titleBadge = isStudying
            ? '<span class="badge-studying">Studying</span>'
            : (oneTimeDone || oneShotLocked)
              ? '<span class="badge-done">Completed</span>'
              : '';

          card.innerHTML = `
            <h3>${escapeHtml(course.name)}${titleBadge}</h3>
            <div class="blurb">${escapeHtml(course.blurb)}</div>
            ${cgTable([
              cgCol('Requires', reqRows),
              cgCol('Reward · on completion', rewardRows),
            ])}
            <button class="btn" data-enroll="${course.key}" ${btnDisabled ? 'disabled' : ''}>
              ${btnLabel}
            </button>
          `;
          grid.appendChild(card);
        }
        section.appendChild(grid);
        catsRoot.appendChild(section);
        // Fold/unfold each tier's course list; state persists per tier.
        makeCollapsible(h2, grid, `scholarium:tier:${tier}`);
      }

      catsRoot.querySelectorAll<HTMLButtonElement>('button[data-enroll]').forEach((btn) => {
        if (btn.disabled) return;
        btn.addEventListener('click', async () => {
          const key = btn.dataset.enroll!;
          const all = catsRoot.querySelectorAll<HTMLButtonElement>('button[data-enroll]');
          all.forEach((b) => (b.disabled = true));
          try {
            const r = await api.enrollInCourse({ courseKey: key });
            store.set({ character: r.character });
            flash = {
              kind: 'success',
              message: `Enrolled in ${r.courseName}. Comes due ${new Date(r.completesAt).toLocaleTimeString()}.`,
            };
            await refreshDynamic();
          } catch (err) {
            const msg = err instanceof HttpError ? err.message : 'Enrol failed.';
            flash = { kind: 'danger', message: msg };
          } finally {
            anchored(renderMain);
          }
        });
      });

      // Make every perk chip clickable — opens a popup describing its effect.
      main.querySelectorAll<HTMLElement>('.schol-perk[data-slug]').forEach((el) => {
        attachPerkInfoToggle(el, el.dataset.slug!);
      });

      // Live countdown — updates the remaining label + progress bar each second.
      clearCountdown();
      if (active && !active.ready) {
        const startMs = Date.parse(active.startedAt);
        const endMs = Date.parse(active.completesAt);
        const totalMs = Math.max(1, endMs - startMs);
        countdownTimer = window.setInterval(async () => {
          const cd = main.querySelector<HTMLSpanElement>('#cd');
          const fill = main.querySelector<HTMLDivElement>('#schol-fill');
          if (!cd) {
            clearCountdown();
            return;
          }
          const remaining = formatRemaining(active!.completesAt);
          cd.textContent = remaining;
          if (fill) {
            const elapsedMs = Math.min(totalMs, Math.max(0, Date.now() - startMs));
            fill.style.width = `${Math.min(100, (elapsedMs / totalMs) * 100).toFixed(1)}%`;
          }
          if (remaining === '0s') {
            clearCountdown();
            // Re-fetch — server will report ready=true.
            await refreshDynamic();
            anchored(renderMain);
          }
        }, 1000);
      }
    };

    anchored(renderMain);
  });

  window.addEventListener('hashchange', clearCountdown, { once: true });
}

function formatRemaining(iso: string): string {
  const ms = Date.parse(iso) - Date.now();
  if (ms <= 0) return '0s';
  const sec = Math.ceil(ms / 1000);
  const d = Math.floor(sec / 86_400);
  const h = Math.floor((sec % 86_400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  if (seconds < 86_400) return `${Math.round(seconds / 3600)} h`;
  const days = Math.round(seconds / 86_400);
  if (days < 14) return `${days} days`;
  const weeks = Math.round(days / 7);
  if (weeks < 5) return `${weeks} weeks`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months} months`;
  const years = Math.round(days / 365);
  return years === 1 ? '1 year' : `${years} years`;
}

